/* eslint max-lines: 0 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import styled from 'styled-components/macro';

import utils from 'utils';
import selectors from 'state/selectors';
import services from 'services';

import Modal from 'components/Modal';
import Dropdown from 'components/Input/dropdown/Dropdown';

export const SelectContainer = styled.div`
  display: flex;
  justify-content: start;
`;

const DropDownContainer = styled.div`
  display: flex;
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  padding-bottom: 15px;
  padding-right: 15px;
  justify-content: flex-end;
  align-items: center;
  label {
    padding-right: 15px;
    font-size: ${({ theme }) => theme.fontSizePalette.xSmall};
  }
`;

interface Option {
  label: string;
  value: number;
}

const CompanyDocType = () => {
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const selectedDocuments = useSelector(selectors.documents.selectAllActiveDocuments);
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const companiesDocTypeUser = useSelector(selectors.app.getCompaniesDocTypeUser);

  const idProgram = useSelector(selectors.app.getProgramCode);
  const [selectedCompany, setSelectedCompany] = useState<Option | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<Option | null>(null);
  const [initialCompany, setInitialCompany] = useState<Option | null>(null);
  const [InitialDocType, setInitialDocType] = useState<Option | null>(null);
  const [errorProtocols, setErrorProtocols] = useState<number[]>([]);
  const [erroModalVisibilyty, setErrorModalVisibility] = useState(false);
  const [ErrorModal] = utils.hooks.useBulkActionErrorModal(errorProtocols, erroModalVisibilyty);

  useEffect(() => {
    if (selectedDocuments.length) {
      const companyOption = companiesDocTypeUser.find((e) => e.idCompany === selectedDocuments[0].idCompany);
      companyOption && setSelectedCompany({ label: companyOption.companyName, value: companyOption.idCompany });
      companyOption && setInitialCompany({ label: companyOption.companyName, value: companyOption.idCompany });

      const docTypes = selectedDocuments.map((e) => e.idDocType);
      const sameDocTypeCheck = docTypes.every((val, i, arr) => val === arr[0]);
      if (sameDocTypeCheck && companyOption) {
        const docTypeValue = companyOption.docType.find((e) => e.idDocType === docTypes[0]);
        docTypeValue && setSelectedDocType({ label: docTypeValue.docName, value: docTypeValue.idDocType });
        docTypeValue && setInitialDocType({ label: docTypeValue.docName, value: docTypeValue.idDocType });
      }
    }
  }, [selectedDocuments, companiesDocTypeUser]);

  const onCompanyChange = (opt: Option | null) => {
    opt && setSelectedCompany(opt);
    setSelectedDocType(null);
  };

  const onDocumentTypeChange = (opt: Option | null) => {
    opt && setSelectedDocType(opt);
  };

  const setCompany = async () => {
    try {
      if (protocols.length && selectedDocType && selectedCompany && selectedCompany?.value !== initialCompany?.value) {
        const { data } = await services.changeCompanyDocument({
          idDocType: selectedDocType.value,
          idCompany: selectedCompany.value,
          protocols: protocols,
          idProgram,
        });
        dispatch(actions.documents.updateTableRows({ protocols, property: 'idCompany', value: selectedCompany.value }));
        return data;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setDocumentType = async () => {
    try {
      if (protocols.length && selectedDocType && selectedCompany && selectedDocType?.value !== InitialDocType?.value) {
        const { data } = await services.changeDocumentType({
          idDocType: selectedDocType.value,
          idCompany: selectedCompany.value,
          protocols: protocols,
          idProgram,
        });

        dispatch(actions.documents.updateTableRows({ protocols, property: 'idDocType', value: selectedDocType.value }));
        return data;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setCompanyAndDocType = async () => {
    const resultChangeCompany = await setCompany();
    const resultDocType = await setDocumentType();
    try {
      // In case both input have changed
      if (resultChangeCompany && resultDocType) {
        // In case both apis have worked
        if (resultChangeCompany.success && resultDocType.success) {
          utils.app.notify('success', t('all_changed_correctly'));
          // update both
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'companyName', value: selectedCompany.label }),
            );
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'documentName', value: selectedDocType.label }),
            );
          // In case both apis haven't worked
          dispatch(actions.modal.closeModal());
        } else if (!resultChangeCompany.success && !resultDocType.success) {
          utils.app.notify('fail', t('not_changed_correctly'));
          // Open modal with merged Array (errors)
          const protocolErrors = [...resultChangeCompany.protocols, ...resultDocType.protocols];
          setErrorProtocols(protocolErrors);
          setErrorModalVisibility(true);

          // update only successful
          const protocolsToChangeCompany = protocols.filter((prot) => !resultChangeCompany.protocols.includes(prot));
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChangeCompany,
                property: 'companyName',
                value: selectedCompany.label,
              }),
            );

          const protocolsToChangeDocument = protocols.filter((prot) => !resultDocType.protocols.includes(prot));
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChangeDocument,
                property: 'documentName',
                value: selectedDocType.label,
              }),
            );
          // In case just company's api hasn't worked
        } else if (!resultChangeCompany?.success && resultDocType.success) {
          // Open modal with company's array
          setErrorProtocols(resultChangeCompany.protocols);
          setErrorModalVisibility(true);
          utils.app.notify('warning', t('just_doc_type_changed_correctly'));

          // update only successful company and all doc types
          const protocolsToChange = protocols.filter((prot) => !resultChangeCompany.protocols.includes(prot));
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChange,
                property: 'companyName',
                value: selectedCompany.label,
              }),
            );
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'documentName', value: selectedDocType.label }),
            );
          // In case just doctype's api hasn't worked
        } else if (resultChangeCompany.success && !resultDocType.success) {
          // Open modal with doctype's array
          setErrorProtocols(resultDocType.protocols);
          setErrorModalVisibility(true);
          utils.app.notify('warning', t('just_company_changed_correctly'));

          // get only successful docType protocols and update all companies
          const protocolsToChange = protocols.filter((prot) => !resultDocType.protocols.includes(prot));
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChange,
                property: 'documentName',
                value: selectedDocType.label,
              }),
            );
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'companyName', value: selectedCompany.label }),
            );
        }
        // In case the company has changed
      } else if (resultChangeCompany && resultDocType === undefined) {
        if (resultChangeCompany.success) {
          utils.app.notify('success', t('company_changed_correctly'));
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'companyName', value: selectedCompany.label }),
            );
          dispatch(actions.modal.closeModal());
        } else {
          // Open modal with company's array
          setErrorProtocols(resultChangeCompany.protocols);
          setErrorModalVisibility(true);
          utils.app.notify('fail', t('company_not_changed_correctly'));

          // update only successful ones
          const protocolsToChange = protocols.filter((prot) => !resultChangeCompany.protocols.includes(prot));
          selectedCompany !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChange,
                property: 'companyName',
                value: selectedCompany.label,
              }),
            );
        }
        // In case the doctype has changed
      } else if (resultChangeCompany === undefined && resultDocType) {
        if (resultDocType.success) {
          utils.app.notify('success', t('doc_type_changed_correctly'));
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({ protocols, property: 'documentName', value: selectedDocType.label }),
            );
          dispatch(actions.modal.closeModal());
        } else {
          // Open modal with doctype's array
          setErrorProtocols(resultDocType.protocols);
          setErrorModalVisibility(true);
          utils.app.notify('fail', t('doc_type_not_changed_correctly'));

          // update only successful ones
          const protocolsToChange = protocols.filter((prot) => !resultDocType.protocols.includes(prot));
          selectedDocType !== null &&
            dispatch(
              actions.documents.updateTableRows({
                protocols: protocolsToChange,
                property: 'documentName',
                value: selectedDocType.label,
              }),
            );
        }
      }
    } catch (e) {
      console.error(e);
      utils.app.notify('fail', t('something_went_wrong'));
    }
  };

  return companiesDocTypeUser ? (
    <>
      {erroModalVisibilyty ? (
        <ErrorModal />
      ) : (
        <>
          <Modal.Header title={t('company_doc_type_title')} subtitle={t('company_doc_type_subtitle')} />
          <Modal.Content>
            <SelectContainer>
              <DropDownContainer>
                <label>{t('company')}</label>
                <Dropdown
                  id="company-dropdown"
                  value={selectedCompany}
                  onChange={onCompanyChange}
                  name="company"
                  options={utils.input.buildOptions(companiesDocTypeUser || [], 'companyName', 'idCompany')}
                />
              </DropDownContainer>
              <DropDownContainer>
                <label>{t('document-type')}</label>
                <Dropdown
                  id="modal-document-type-dropdown"
                  value={selectedDocType}
                  onChange={onDocumentTypeChange}
                  name="document-type"
                  options={utils.input.buildOptions(
                    companiesDocTypeUser.find((e) => e.idCompany === selectedCompany?.value)?.docType || [],
                    'docName',
                    'idDocType',
                  )}
                />
              </DropDownContainer>
            </SelectContainer>
          </Modal.Content>
          <Modal.Footer
            confirmText={t('continua')}
            confirmAction={setCompanyAndDocType}
            confirmDisabled={
              !selectedCompany ||
              !selectedDocType ||
              (selectedDocType?.value === InitialDocType?.value && selectedCompany?.value === initialCompany?.value)
            }
            closeButtonText={t('annulla')}
          />
        </>
      )}
    </>
  ) : null;
};

export default CompanyDocType;
